// Shared presentation helpers and legacy browser-review primitives. New trace
// review surfaces read persisted backend reports; the remaining run helpers
// are retained for Findings Review and compatibility with cached results.
import { runTraceEvaluation, severityRank, sensitivityRank } from "./traceEvaluation.js";
import { defaultTaskModelFor, lookupCatalogModel } from "./modelCatalog.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

// Completed reports, so re-opening a trace — or asking the other surface for
// the same reading — doesn't re-spend an agent run. Bounded, evict-oldest.
const RESULT_CACHE = new Map();
const RESULT_CACHE_MAX = 20;
onAuthIdentityChange(() => RESULT_CACHE.clear());

// Every input that can change the answer is in the key, so a run under a
// different policy version, model, or reader mode is a different report rather
// than a stale hit.
export function reviewCacheKey({ trace, constitution, discovered, model, split }) {
    return `${getAuthIdentityEpoch()}:${trace?.id || ""}:${constitution?.updatedAt || 0}:${discovered?.discoveredAt || 0}:${model || ""}:${split ? "split" : "combined"}`;
}

export function readReviewCache(key) {
    return RESULT_CACHE.get(key) || null;
}

export function writeReviewCache(key, report) {
    if (RESULT_CACHE.size >= RESULT_CACHE_MAX) {
        const oldest = RESULT_CACHE.keys().next().value;
        if (oldest !== undefined) RESULT_CACHE.delete(oldest);
    }
    RESULT_CACHE.set(key, report);
}

// Combined (one judge carrying every document) vs split (isolated baseline, one
// pass per document, second reader). A workflow preference, not a per-trace
// choice, so it persists per browser — and the lanes button obeys it rather
// than offering the choice a second time.
const SPLIT_PREF_KEY = "origin.trace-review.mode.v1";

export function readReviewSplit() {
    try { return window.localStorage?.getItem(SPLIT_PREF_KEY) === "split"; } catch { return false; }
}

export function writeReviewSplit(split) {
    try { window.localStorage?.setItem(SPLIT_PREF_KEY, split ? "split" : "combined"); } catch { /* private mode */ }
}

// The judging model when the reader hasn't picked one. Defaults to the PRIMARY
// chat model, not the cheap task model the influence tracer uses: this is the
// expensive read the triage gate escalates to, and a weaker judge silently
// returns fewer incidents — observed on a credential-exposure trace that the
// task model cleared and the primary model flagged. The ledger's picker can
// still step down deliberately.
export function defaultReviewModel(creds) {
    if (!creds) return "";
    return creds.model || creds.taskModel || defaultTaskModelFor(creds.provider);
}

// One launch, one argument set. Both surfaces route through here so a change to
// how a review is run cannot apply to only one of them.
export function startTraceReview({
    trace,
    creds = null,
    constitution = null,
    discovered = null,
    model = null,
    split = false,
    signal,
    onPass = null,
}) {
    return runTraceEvaluation({
        trace,
        constitutionText: constitution?.text || "",
        discoveredNorms: discovered?.norms || [],
        discoveredIntents: discovered?.intents || [],
        model: model || null,
        split,
        // The claim-verify pass runs on the cheap task model: the verify-shape
        // measurement showed model tier doesn't change the answer to "does this
        // stated claim hold", so the expensive model buys nothing there.
        verifyModel: creds?.taskModel || null,
        // Lets the judge size its transcript window to the model it actually
        // runs on rather than the smallest plausible one.
        contextWindow: lookupCatalogModel(creds?.provider, model)?.contextWindow || null,
        signal,
        onPass,
    });
}

// ── reading a finished report ───────────────────────────────────────
// Colour is a judgment about severity, so it is defined once: the ledger and
// the lanes strip drifting apart on what counts as "danger" would make the same
// finding look like two different findings.
export const SEVERITY_COLOR = {
    danger: "var(--danger, #d4503e)",
    warn: "var(--warn, #c08a2d)",
    calm: "var(--muted)",
};

export function behaviourTone(severity) {
    if (severity === "critical" || severity === "high") return "danger";
    if (severity === "medium") return "warn";
    return "calm";
}

export function contentTone(sensitivity) {
    if (sensitivity === "restricted") return "danger";
    if (sensitivity === "confidential") return "warn";
    return "calm";
}

export function incidentTone(inc) {
    const b = inc.behaviour ? behaviourTone(inc.behaviour.severity) : "calm";
    const c = inc.content ? contentTone(inc.content.sensitivity) : "calm";
    const rank = (t) => (t === "danger" ? 2 : t === "warn" ? 1 : 0);
    return rank(b) >= rank(c) ? b : c;
}

function toneRank(tone) {
    return tone === "danger" ? 2 : tone === "warn" ? 1 : 0;
}

// Incidents collapsed onto the turn axis, one entry per turn that carries
// evidence. The report cites turns 1-based (locatedTurn); the lanes axis is
// 0-based, and that conversion happens HERE so no caller can get it wrong.
//
// Uncited incidents are dropped: they assert nothing the reader can navigate
// to, and a mark on the axis is a promise that there is something to read at
// that turn. Out-of-range turns are dropped for the same reason — a citation
// past the end of the axis has no cell to sit in.
export function reviewMarks(report, turnCount) {
    const byTurn = new Map();
    for (const inc of report?.incidents || []) {
        if (inc.uncited) continue;
        for (const cited of inc.turns || []) {
            const turn = cited - 1;
            if (!(turn >= 0 && turn < turnCount)) continue;
            const at = byTurn.get(turn) || { turn, incidents: [], tone: "calm" };
            at.incidents.push(inc);
            const tone = incidentTone(inc);
            if (toneRank(tone) > toneRank(at.tone)) at.tone = tone;
            byTurn.set(turn, at);
        }
    }
    return [...byTurn.values()].sort((a, b) => a.turn - b.turn);
}

// Marks closer together than a mark is wide, merged into one. The same
// detail-for-density trade the lane marks make: at a zoom where two dots would
// overlap, one dot carrying both findings is honest and clickable, where two
// smeared dots are neither. `through` is the last turn a mark speaks for.
export function mergeReviewMarks(marks, cellW, minPx) {
    const out = [];
    for (const mark of marks) {
        const prev = out[out.length - 1];
        if (prev && cellW > 0 && minPx > 0 && (mark.turn - prev.turn) * cellW < minPx) {
            for (const inc of mark.incidents) {
                if (!prev.incidents.includes(inc)) prev.incidents.push(inc);
            }
            if (toneRank(mark.tone) > toneRank(prev.tone)) prev.tone = mark.tone;
            prev.through = mark.turn;
            continue;
        }
        out.push({ ...mark, incidents: [...mark.incidents], through: mark.turn });
    }
    return out;
}

// The strip's one-line verdict. A clean report and an unrun one must not read
// the same: "clean" is a judgment, "not run" is the absence of one.
export function reviewSummaryLine(report) {
    if (!report) return "";
    const cited = (report.incidents || []).filter((inc) => !inc.uncited);
    if (!cited.length) return report.withheld > 0 ? "nothing to act on" : "clean";
    const worstBehaviour = cited.reduce((w, i) => (severityRank(i.behaviour?.severity) > severityRank(w) ? i.behaviour.severity : w), null);
    const worstContent = cited.reduce((w, i) => (sensitivityRank(i.content?.sensitivity) > sensitivityRank(w) ? i.content.sensitivity : w), null);
    const worst = [worstBehaviour, worstContent].filter((v) => v && v !== "none").join(" · ");
    const count = `${cited.length} finding${cited.length === 1 ? "" : "s"}`;
    return worst ? `${count} · ${worst}` : count;
}
